//#region ENUM LocalStorageKeysEnum
/**
 * ### LocalStorageKeysEnum
 * Enum defining the keys used for storing values in the browser's local storage.
 *
 * These keys standardize and centralize access to specific local storage entries,
 * improving consistency and reducing the risk of typos across the codebase.
 *
 * @enum LocalStorageKeysEnum
 * @property authPersistence - Key for authentication persistence data.
 * @property authLastLogin - Key for storing last login timestamp.
 * @property agGridColDefOrderNgpReport - Key for AG Grid column order in NGP Report.
 * @property agGridColDefOrderStockManager - Key for AG Grid column order in Stock Manager.
 * @property agGridColDefSizeNgpReport - Key for AG Grid column sizes in NGP Report.
 * @property agGridColDefSizeStockManager - Key for AG Grid column sizes in Stock Manager.
 * @property aoOpenOrder - Key for storing the currently open auto order.
 * @property barcodesScannedBinLocationUpdate - Key for scanned barcodes in Bin Location Update feature.
 * @property barcodesScannedOrderCreate - Key for scanned barcodes in Order Create feature.
 * @property barcodesScannedProcessGRV - Key for scanned barcodes in Process GRV feature.
 * @property barcodesScannedShelfTalkersPrint - Key for scanned barcodes in Shelf Talkers Print feature.
 * @property barcodesScannedStockUpdate - Key for scanned barcodes in Stock Update feature.
 * @property barcodeScannerVisibleFieldsStockUpdate - Key for user's visible field preferences in Stock Update feature.
 * @property isFitToGridStockManager - Auto-fit toggle for Stock Manager.
 * @property isFitToGridNgpReport - Auto-fit toggle for NGP Report.
 * @property isFitToGridAutoOrder - Auto-fit toggle for Auto Ordering.
 */
export enum LocalStorageKeysEnum {
  authPersistence = 'authPersistence',
  authLastLogin = 'authLastLogin',
  agGridColDefOrderNgpReport = 'agGridColDefOrderNgpReport',
  agGridColDefOrderStockManager = 'agGridColDefOrderStockManager',
  agGridColDefSizeNgpReport = 'agGridColDefSizeNgpReport',
  agGridColDefSizeStockManager = 'agGridColDefSizeStockManager',
  agGridColDefOrderAddSupplier = 'agGridColDefOrderAddSupplier',
  agGridColDefSizeAddSupplier = 'agGridColDefSizeAddSupplier',
  agGridColDefOrderAutoOrder = 'agGridColDefOrderAutoOrder',
  agGridColDefOrderQuotes = 'agGridColDefOrderQuotes',
  agGridColDefSizeQuotes = 'agGridColDefSizeQuotes',
  agGridColDefSizeAutoOrder = 'agGridColDefSizeAutoOrder',
  aoOpenOrder = 'aoOpenOrder',
  barcodesScannedBinLocationUpdate = 'barcodesScannedBinLocationUpdate',
  barcodesScannedOrderCreate = 'barcodesScannedOrderCreate',
  barcodesScannedProcessGRV = 'barcodesScannedProcessGRV',
  barcodesScannedShelfTalkersPrint = 'barcodesScannedShelfTalkersPrint',
  barcodesScannedStockUpdate = 'barcodesScannedStockUpdate',
  barcodeScannerVisibleFieldsStockUpdate = 'barcodeScannerVisibleFieldsStockUpdate',
  agGridColDefOrderStockTake = 'agGridColDefOrderStockTake',
  agGridColDefSizeStockTake = 'agGridColDefSizeStockTake',
  agGridColDefOrderStockTakeStockInfo = 'agGridColDefOrderStockTakeStockInfo',
  agGridColDefSizeStockTakeStockInfo = 'agGridColDefSizeStockTakeStockInfo',
  agGridColDefOrderAutoOrderNgpReport = 'agGridColDefOrderAutoOrderNgpReport',
  agGridColDefSizeAutoOrderNgpReport = 'agGridColDefSizeAutoOrderNgpReport',
  isFitToGridStockManager = 'isFitToGridStockManager',
  isFitToGridNgpReport = 'isFitToGridNgpReport',
  isFitToGridAutoOrder = 'isFitToGridAutoOrder',
  acknowledgedDisabledStores = 'acknowledgedDisabledStores',
}

//#endregion

//#region TYPE LocalStorageKeyType
/**
 * ### LocalStorageKeyType
 * Template literal type representing valid keys from the `LocalStorageKeysEnum`.
 *
 * This type enforces that only string values defined in `LocalStorageKeysEnum`
 * can be used when working with local storage keys in a type-safe manner.
 *
 * @type LocalStorageKeyType
 * @example
 * // Valid examples:
 * let key1: LocalStorageKeyType = 'authPersistence';
 * let key2: LocalStorageKeyType = 'barcodesScannedOrderCreate';
 */
export type LocalStorageKeyType = `${LocalStorageKeysEnum}`;
//#endregion
